/* eslint-disable react/jsx-props-no-spreading */
import { forwardRef } from 'react';
// Utils & misc
import cn from '../../../utils/cn';
// Style
import style from './Textarea.module.css';

/**
 * @typedef {object} TextareaProps
 * @property {boolean} [autoScale=false] - Whether textarea should auto-resize to content
 * @property {boolean} [hasError=false] - Whether textarea has validation error
 * @property {string} [className] - Additional CSS classes
 * @property {string} [placeholder] - Placeholder text
 * @property {boolean} [disabled=false] - Whether textarea is disabled
 * @property {boolean} [readOnly=false] - Read-only (posted, not user-editable)
 * @property {'default' | 'light'} [theme = 'default'] - Theme of the textarea
 */

/**
 * Textarea component - A multi-line text input with optional auto-scaling
 * @param {TextareaProps & React.TextareaHTMLAttributes<HTMLTextAreaElement>} props - Component props
 * @param {React.RefObject<HTMLTextAreaElement>} ref - Forwarded ref to the textarea element
 * @returns {JSX.Element} Textarea component
 */
function TextareaComponent({
  autoScale = false,
  hasError = false,
  placeholder = '',
  disabled = false,
  readOnly = false,
  className,
  theme = 'light',
  ...props
}, ref) {
  const current = ref?.current;
  return (
    <textarea
      ref={ref}
      placeholder={placeholder}
      className={cn([
        style.input,
        style[theme],
        hasError && style['has-error'],
        disabled && style['is-disabled'],
        readOnly && style['read-only'],
        className,
      ])}
      disabled={disabled}
      readOnly={readOnly}
      rows={0}
      {...props}
      style={
        autoScale
          ? { height: current?.scrollHeight, overflow: 'hidden' }
          : null
      }
    />
  );
}

const Textarea = forwardRef(TextareaComponent);

export default Textarea;
